import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { KalDialogConfig, KalDialogRef, KalDialogService, KalSnackbarService } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import { tap, catchError, Observable, switchMap, throwError } from 'rxjs';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { FormValidator } from 'src/app/validators/form-validator';
import { MutationSaveListeVilleArgs, ArrayOfVilleDto, ArrayOfPaysDto, MutationGetAllPaysArgs, VilleDtoInput, StatusDtoEnum } from 'src/graphql/generated-types';
import {
    SharedAddCountryDialogComponent,
    SharedAddCountryDialogData
} from 'src/app/_shared/dialogs/shared-add-new-country-dialog/shared-add-country-dialog.component';

type SharedAddCityFormType = {
    postalCode: FormControl<string | null>;
    inseeCode: FormControl<string | null>;
    cityName: FormControl<string | null>;
    cityId: FormControl<number | null>;
    countryId: FormControl<number | null>;
    latitude: FormControl<number | null>;
    longitude: FormControl<number | null>;
}

type Country = {
    id: number;
    name: string;
}

export class SharedAddCityDialogData {
    accepted?: boolean;
}

const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;

const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;

@Component({
    selector: 'app-shared-add-new-city-dialog',
    templateUrl: './shared-add-city-dialog.component.html',
    styleUrls: ['./shared-add-new-city-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAddCityDialogComponent extends BaseComponent implements OnInit {

    form: FormGroup<SharedAddCityFormType>;
    countryList: Country[] = [];

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly dialogRef: KalDialogRef<SharedAddCityDialogComponent>,
                private readonly graphqlService: GraphqlService,
                private readonly dialogService: KalDialogService,
                private readonly snackbarService: KalSnackbarService,
                private readonly fb: FormBuilder) {
        super(cdr);
    }

    private getCountryList$(): Observable<ArrayOfPaysDto> {
        return this.graphqlService.mutateOne<'GetAllPays', MutationGetAllPaysArgs>({
            mutation: gql`
                mutation GetAllPays($contexte: ContextInfosInput ) {
                    GetAllPays(contexte: $contexte ) {
                        PaysDTO {
                            Id
                            Libelle
                        }
                    }
                }
          `
        }).pipe(
            tap((countryList: ArrayOfPaysDto) =>
                this.countryList = (countryList?.PaysDTO || []).map((country) => ({
                    id: country.Id,
                    name: country.Libelle
                }))
            )
        );
    }

    private normalize(): VilleDtoInput {
        const {
            postalCode,
            inseeCode,
            cityName,
            countryId,
            latitude,
            longitude
        } = this.form.value;

        return {
            CodePostal: postalCode || '',
            CodeInsee: inseeCode || '',
            Libelle: cityName || '',
            Pays: {
                Id: countryId || 0
            },
            Latitude: +latitude || 0,
            Longitude: +longitude || 0,
            Status: StatusDtoEnum.Inserted
        };
    }

    openCountryAddDialog(): void {
        const dialogConfig = new KalDialogConfig<SharedAddCountryDialogData>({
            title: 'Ajouter un nouveau pays',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop'
        });

        this.watch$ = this.dialogService.open(SharedAddCountryDialogComponent, dialogConfig).afterClosed.pipe(
            switchMap(() => this.wrapLoader$(this.getCountryList$()))
        );
    }

    submit(): void {
        this.loading = true;
        this.watch$ = this.graphqlService.mutateOne<'SaveListeVille', MutationSaveListeVilleArgs>({
            mutation: gql`
                mutation SaveListeVille($contexte: ContextInfosInput, $ville: ArrayOfVilleDTOInput, $Criteres: CriteresVilleDTOInput) {
                    SaveListeVille(contexte: $contexte, ville: $ville, Criteres: $Criteres) {
                        VilleDTO {
                            Id
                            Libelle
                        }
                    }
                }
            `,
            variables: {
                ville: {
                    VilleDTO: [this.normalize()]
                },
                Criteres: {
                    TakeDepartement: false,
                    TakePays: true,
                    TakeRegion: false
                }
            }
        }).pipe(
            tap((city: ArrayOfVilleDto) => {
                this.snackbarService.open({title: `La ville ${city.VilleDTO[0].Libelle} a été ajoutée.`});
                this.loading = false;
                this.dialogRef.close({accepted: true});
            }),
            catchError(err => {
                this.loading = false;
                return throwError(() => err);
            })
        );
    }

    cancel(): void {
        this.dialogRef.close({accepted: false});
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            postalCode: [null, [FormValidator.isNotNull]],
            inseeCode: [null],
            cityName: [null, [FormValidator.isNotNull]],
            cityId: [null],
            countryId: [null, [FormValidator.isNotNull]],
            latitude: [null, [Validators.min(MIN_LATITUDE), Validators.max(MAX_LATITUDE)]],
            longitude: [null, [Validators.min(MIN_LONGITUDE), Validators.max(MAX_LONGITUDE)]]
        }) as FormGroup<SharedAddCityFormType>;

        this.watch$ = this.wrapLoader$(this.getCountryList$());
    }
}
